import React, { createContext, useState, useContext, ReactNode } from "react";
import { User, Workspace } from "./types"; // Assuming Workspace type is defined in 'types'
import Cookies from "js-cookie";
import { verifyAdmin } from "../client/api";
import { clearSsoCookie } from "../utils/ssoCookie";

interface UserContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>> | any;
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: React.Dispatch<React.SetStateAction<Workspace | null>>;
  logout: () => void;
  isAdmin: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(
    null
  );
  const [isAdmin, setIsAdmin] = useState(false);

  const logout = () => {
    setUser(null);
    setCurrentWorkspace(null); // Reset workspace on logout

    // Remove the authInfo cookie
    Cookies.remove("authInfo", {
      domain: process.env.REACT_APP_COOKIE_DOMAIN,
      path: "/",
    });
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    clearSsoCookie();
  };

  const checkAdminStatus = async () => {
    try {
      const adminStatus = await verifyAdmin();
      setIsAdmin(adminStatus);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const updateUser = (userData: User | null) => {
    setUser(userData);
    if (userData?.isAppAdmin) {
      checkAdminStatus();
    } else {
      setIsAdmin(false);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: updateUser,
        currentWorkspace,
        setCurrentWorkspace,
        logout,
        isAdmin,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
